import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableRow from '@material-ui/core/TableRow'
import Button from '@material-ui/core/Button'
import Select from '@material-ui/core/Select'
import {Typography, OutlinedInput, TableHead} from '@material-ui/core'
import MenuItem from '@material-ui/core/MenuItem'

import UsersOrgTable from 'pages/CustomerManagement/Inquiry/UsersOrg'
import useCustomerOrg from 'features/invoice/useInvoiceInquiry'
import {toParsedDate} from 'helpers/dateHelper'
import {zeroPad} from 'helpers/commonHelper'
import RouteConstant from 'constants/RouteConstant'
import useToolbarStyles, {
  useOutlinedInputStyles,
} from 'components/Table/useTableToolbarStyles'
import useStyles from 'components/Table/useTableStyles'
import Pagination from 'components/molcules/Pagination'
import {getCountryFromCountryCode} from 'helpers/uiHelper'
import {
  OrgDetailResponse,
  searchAllApi,
  SearchAllRequest,
  SearchAllResponse,
} from 'api/salesApi'
import {getTextRole} from 'helpers/invoiceHelper'

function TableToolbar() {
  const {t} = useTranslation()
  const classes = useToolbarStyles()
  const outlinedInputClasses = useOutlinedInputStyles()
  const [searchKeyword, setSearchKeyword] = useState('')
  const [searchType, setSearchType] = useState('orgName')
  const {onSearch} = useCustomerOrg()
  const TYPE_SEARCH_ORG = [
    {
      name: t('ICustomerOrganizationName'),
      oid: 'orgName',
    },
    {
      name: t('IEmail'),
      oid: 'userEmail',
    },
  ]

  const handleSearch = () => {
    if (!searchKeyword) {
      onSearch({page: 0, size: 10})
      return
    }
    if (searchType === 'orgName')
      onSearch({orgName: searchKeyword, page: 0, size: 10})
    else if (searchType === 'userEmail')
      onSearch({userEmail: searchKeyword, page: 0, size: 10})
  }

  const handleChangeStatus = (value: string) => {
    setSearchType(value)
  }

  useEffect(() => {
    onSearch({page: 0, size: 10})
  }, [])

  return (
    <div className={classes.root}>
      <div className={classes.searchOrgWrap}>
        <div style={{display: 'flex', gap: 2, justifyContent: 'space-between'}}>
          <Select
            value={searchType || TYPE_SEARCH_ORG[0]?.oid}
            displayEmpty
            className={classes.searchSelectType}
            onChange={(event) => {
              handleChangeStatus(event.target.value as string)
            }}
            input={<OutlinedInput classes={outlinedInputClasses} />}
          >
            {TYPE_SEARCH_ORG?.map((item) => (
              <MenuItem value={item.oid} key={item.oid}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className={classes.searchWrap}>
          <input
            placeholder={t('ISearch')}
            className={classes.searchInput}
            value={searchKeyword}
            onChange={(event) => {
              const value = event?.currentTarget?.value
              setSearchKeyword(value)
            }}
          />
          <span className={classes.searchDivider} />
          <button
            type='submit'
            onClick={handleSearch}
            color='primary'
            className={classes.searchButton}
          >
            {t('ISearch')}
          </button>
        </div>
      </div>
    </div>
  )
}

function PageTable() {
  const {t, i18n} = useTranslation()
  const classes = useStyles()
  const history = useHistory()
  const {loading, query, pagingInfo, items, search, onSearch} = useCustomerOrg()
  const [listUserByAgency, setListUserByAgency] =
    useState<SearchAllResponse[]>()
  const [totalUserPages, setTotalPages] = useState(0)
  const [currentOrg, setCurrentOrg] = useState<OrgDetailResponse | null>(null)
  const formatDate = (date: Date) => {
    if (!date) return ''
    const {years, months, days} = toParsedDate(date)
    return `${years}-${zeroPad(months)}-${zeroPad(days)}`
  }

  const setPageIndex = (pageNumber: number) => {
    onSearch({
      ...search,
      page: pageNumber,
    })
  }

  const onSizeChange = (
    event: React.ChangeEvent<{name?: string; value: unknown}>,
  ) => {
    const pageSize = parseInt(event.target.value as string, 10)
    onSearch({
      ...search,
      page: 0,
      size: pageSize,
    })
  }

  const getOrgTypeLabel = (type: OrganizationType) => {
    if (type === 'NORMAL') return t('IHospital')
    if (type === 'HEALTH') return t('IExaminationCenter')
    return ''
  }

  const handleNavigateEditMember = (orgId: number) => {
    history.push(`${RouteConstant.MEMBER_EDIT.path}?orgId=${orgId}`)
  }

  const handleGetAgencyUsersOrgs = async (orgId: number) => {
    const res = await searchAllApi({
      orgId,
      page: 0,
      size: 10,
    })
    setListUserByAgency(res.page.content)
    setTotalPages(res.page.totalPages)
  }

  const onSearchUser = async (payload: SearchAllRequest) => {
    const res = await searchAllApi(payload)
    setListUserByAgency(res.page.content)
  }

  return (
    <div>
      <TableContainer>
        <TableToolbar />
        <div>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow className={classes.tableHeader}>
                  <TableCell align='center' className={classes.cellValue}>
                    {t('IListProductUse')}
                  </TableCell>
                  <TableCell align='center' className={classes.cellValue}>
                    {t('IOrgNameShort')}
                  </TableCell>
                  <TableCell align='center' className={classes.cellValue}>
                    {t('IEmail')}
                  </TableCell>
                  <TableCell align='center' className={classes.cellValue}>
                    {t('ICeoName')}
                  </TableCell>
                  <TableCell align='center' className={classes.cellValue}>
                    {t('ICountry')}
                  </TableCell>
                  <TableCell align='center' className={classes.cellValue}>
                    {t('IPhone')}
                  </TableCell>
                  <TableCell align='center' className={classes.cellValue}>
                    {t('IAddress')}
                  </TableCell>
                  <TableCell align='center' className={classes.cellValue}>
                    {t('IAuthority')}
                  </TableCell>
                  <TableCell align='center' className={classes.cellValue}>
                    {t('IJoinDate')}
                  </TableCell>
                  <TableCell align='center' className={classes.cellValue}>
                    {t('IAgency')}
                  </TableCell>
                  <TableCell align='center' className={classes.cellValue}>
                    {t('IEditMember')}
                  </TableCell>
                  <TableCell align='center' className={classes.cellValue}>
                    {t('IUser')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow>
                    <TableCell align='center' className={classes.cellValue}>
                      {getOrgTypeLabel(item?.org?.type as OrganizationType)}
                    </TableCell>
                    <TableCell align='center' className={classes.cellValue}>
                      {item.org?.name ?? ''}
                    </TableCell>
                    <TableCell align='center' className={classes.cellValue}>
                      {item?.orgDetail?.email ?? ''}
                    </TableCell>
                    <TableCell align='center' className={classes.cellValue}>
                      {['ko', 'ja', 'zh'].includes(i18n.language)
                        ? `${item.chiefUser?.lastName ?? ''} ${
                            item.chiefUser?.firstName ?? ''
                          }`
                        : `${item.chiefUser?.firstName ?? ''} ${
                            item.chiefUser?.lastName ?? ''
                          }`}
                    </TableCell>
                    <TableCell align='center' className={classes.cellValue}>
                      {getCountryFromCountryCode(item.org?.countryCode ?? '')}
                    </TableCell>
                    <TableCell align='center' className={classes.cellValue}>
                      {item?.orgDetail?.tel}
                    </TableCell>
                    <TableCell align='center' className={classes.cellValue}>
                      {item.org?.address ?? ''}
                    </TableCell>
                    <TableCell align='center' className={classes.cellValue}>
                      {getTextRole(item?.orgDetail?.roleLevel)}
                    </TableCell>
                    <TableCell align='center' className={classes.cellValue}>
                      {formatDate(new Date(item.org?.createdAt ?? 0))}
                    </TableCell>
                    <TableCell align='center' className={classes.cellValue}>
                      {item.agency
                        ? `${item.agency.name} (${item.agency.email})`
                        : '-'}
                    </TableCell>
                    <TableCell align='center' className={classes.cellValue}>
                      <Button
                        variant='contained'
                        color='primary'
                        disableElevation
                        onClick={() =>
                          handleNavigateEditMember(item.org?.id ?? 0)
                        }
                      >
                        {t('IEdit')}
                      </Button>
                    </TableCell>
                    <TableCell align='center' className={classes.cellValue}>
                      <Button
                        variant='contained'
                        color='primary'
                        disableElevation
                        onClick={() => {
                          handleGetAgencyUsersOrgs(item?.org?.id ?? 0)
                          setCurrentOrg(item)
                        }}
                      >
                        {t('IInquirySelect')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {pagingInfo.totalPages > 0 && (
            <Pagination
              totalPageCount={pagingInfo.totalPages}
              currentPageIndex={query.paging.number ?? 0}
              itemCountPerPage={query.paging.size ?? 10}
              setCurrentPageIndex={setPageIndex}
              loading={loading}
              onItemCountPerPageChanged={onSizeChange}
            />
          )}
        </div>
        <Typography className={classes.padding} />
        <UsersOrgTable
          listUserByAgency={listUserByAgency ?? []}
          currentOrg={currentOrg}
          onSearch={onSearchUser}
          totalPages={totalUserPages}
        />
        <Typography className={classes.padding} />
      </TableContainer>
    </div>
  )
}

export default PageTable
